import { useEffect, useState } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useSignalR } from "../providers/signalr-context";
import { HubConnectionState } from "../services/signalr";

// Grace period before surfacing a degraded connection. Auto-reconnect handles
// transient blips silently — only sustained failures (expired auth, blocked
// WebSocket, extended outage) should bother the user.
const DEGRADED_GRACE_MS = 30_000;

// Surfaces the SignalR connection state so users know when live updates are
// genuinely broken. Hidden during the happy path AND during short blips.
export function RealtimeStatusBadge() {
  const { connectionState } = useSignalR();

  if (connectionState === HubConnectionState.Connected) return null;

  return <DegradedRealtimeStatus key={connectionState} connectionState={connectionState} />;
}

function DegradedRealtimeStatus({ connectionState }: { connectionState: HubConnectionState }) {
  const [showBadge, setShowBadge] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setShowBadge(true), DEGRADED_GRACE_MS);
    return () => window.clearTimeout(timer);
  }, []);

  if (!showBadge) {
    return null;
  }

  const isReconnecting =
    connectionState === HubConnectionState.Reconnecting ||
    connectionState === HubConnectionState.Connecting;

  const label = isReconnecting ? "Reconnecting…" : "Live updates offline";
  const tooltip = isReconnecting
    ? "Reconnecting to live updates. Data may be a few seconds behind."
    : "Live updates are offline. Refresh the page to see the latest data.";

  const dotClass = isReconnecting
    ? "bg-amber-500 animate-pulse"
    : "bg-red-500";
  const textClass = isReconnecting
    ? "text-amber-700 dark:text-amber-400"
    : "text-red-700 dark:text-red-400";

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs",
            textClass
          )}
          role="status"
          aria-live="polite"
        >
          <span className={cn("size-2 rounded-full", dotClass)} aria-hidden />
          {label}
        </span>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}
